import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { X, Zap } from 'lucide-react'
import { AGENT_ONBOARDING_STEPS, OWNER_ONBOARDING_STEPS } from '../lib/steps'
import { AssignAgentDemoDialog } from './AssignAgentDemoDialog'

const ALL_STEPS = [...OWNER_ONBOARDING_STEPS, ...AGENT_ONBOARDING_STEPS]

interface Rect {
  top: number
  left: number
  width: number
  height: number
}

export function OnboardingHighlight() {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const [rect, setRect] = useState<Rect | null>(null)
  const [stepId, setStepId] = useState<string | null>(null)
  const rafRef = useRef<number>(0)

  const params = new URLSearchParams(location.search)
  const activeOnboard = params.get('onboard')
  const step = activeOnboard ? ALL_STEPS.find((s) => s.id === activeOnboard) : null

  const dismiss = useCallback(() => {
    const next = new URLSearchParams(location.search)
    next.delete('onboard')
    const newSearch = next.toString()
    navigate(
      { pathname: location.pathname, search: newSearch ? `?${newSearch}` : '' },
      { replace: true },
    )
  }, [location.search, location.pathname, navigate])

  useEffect(() => {
    if (!step?.targetAttr) {
      setRect(null)
      setStepId(null)
      return
    }

    setStepId(step.id)

    const findAndUpdate = () => {
      const el = document.querySelector<HTMLElement>(`[data-onboarding-target="${step.targetAttr}"]`)
      if (el) {
        const r = el.getBoundingClientRect()
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height })
      } else {
        rafRef.current = requestAnimationFrame(findAndUpdate)
      }
    }

    rafRef.current = requestAnimationFrame(findAndUpdate)

    const handleResize = () => {
      const el = document.querySelector<HTMLElement>(`[data-onboarding-target="${step.targetAttr}"]`)
      if (el) {
        const r = el.getBoundingClientRect()
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height })
      }
    }

    window.addEventListener('resize', handleResize)
    return () => {
      cancelAnimationFrame(rafRef.current)
      window.removeEventListener('resize', handleResize)
    }
  }, [step])

  useEffect(() => {
    if (!activeOnboard) return
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') dismiss() }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [activeOnboard, dismiss])

  useEffect(() => {
    if (!activeOnboard || !step?.targetAttr) return
    const handleClick = (e: MouseEvent) => {
      const el = document.querySelector<HTMLElement>(`[data-onboarding-target="${step.targetAttr}"]`)
      if (el?.contains(e.target as Node)) setTimeout(dismiss, 200)
    }
    document.addEventListener('click', handleClick, { capture: true })
    return () => document.removeEventListener('click', handleClick, { capture: true })
  }, [activeOnboard, step, dismiss])

  // Special case: assign step shows a demo dialog instead of a ring
  if (activeOnboard === 'assign') {
    return <AssignAgentDemoDialog open={true} onClose={dismiss} />
  }

  if (!step || !rect || !stepId) return null

  const PADDING = 8
  const popoverWidth = 256
  const viewportWidth = window.innerWidth

  const ringTop = rect.top - PADDING
  const ringLeft = rect.left - PADDING
  const ringWidth = rect.width + PADDING * 2
  const ringHeight = rect.height + PADDING * 2

  let popoverLeft = rect.left + rect.width / 2 - popoverWidth / 2
  if (popoverLeft < 8) popoverLeft = 8
  if (popoverLeft + popoverWidth > viewportWidth - 8) popoverLeft = viewportWidth - popoverWidth - 8

  const popoverTop = rect.top + rect.height + PADDING + 6
  const fitsBelow = popoverTop + 130 < window.innerHeight
  const finalPopoverTop = fitsBelow ? popoverTop : rect.top - PADDING - 6 - 118

  return createPortal(
    <>
      {/* Single amber ring — no class applied to the element itself */}
      <div
        className="pointer-events-none fixed z-[9998] onboarding-ring-overlay"
        style={{
          top: ringTop,
          left: ringLeft,
          width: ringWidth,
          height: ringHeight,
          borderRadius: 'var(--radius)',
        }}
      />

      {/* Amber alert popover */}
      <div
        className="fixed z-[9999] w-64 shadow-xl"
        style={{
          top: finalPopoverTop,
          left: popoverLeft,
          borderRadius: 'var(--radius)',
          background: '#f59e0b',
          border: '1.5px solid #d97706',
        }}
      >
        <button
          type="button"
          onClick={dismiss}
          className="absolute right-2 top-2 p-0.5 text-amber-900/70 hover:text-amber-950 transition-colors"
          style={{ borderRadius: 'calc(var(--radius) / 2)' }}
          aria-label="Close"
        >
          <X className="h-3.5 w-3.5" />
        </button>

        <div className="flex items-start gap-2 p-3">
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-950/15">
            <Zap className="h-3 w-3 text-amber-950" />
          </span>
          <div className="min-w-0 pr-4">
            <p className="text-xs font-bold text-amber-950">{t(`${step.i18nKey}.label`)}</p>
            <p className="mt-0.5 text-[11px] leading-snug text-amber-900">{t(`${step.i18nKey}.hint`)}</p>
          </div>
        </div>
      </div>
    </>,
    document.body,
  )
}
